import React from 'react';

import LensMark from './LensMark';
import { BRAND_COLORS, FONT_STACK_SUISSE_INTL } from './brandTokens';

export interface AskRichardWordmarkProps {
  /** Font size in px. The stop and its gap scale with it (em-based). Defaults to 28. */
  size?: number;
  /** On an Ocean Teal field: "ask" becomes Sandy Beige, "Richard" becomes Compass White and the stop's ring turns white. The dot is unchanged. */
  inverted?: boolean;
  /** Draws the Lens ring-dot as the stop after "askRichard". Defaults to true, and is always forced off below 14px, where the ring stops reading. */
  withStop?: boolean;
  className?: string;
}

/** Below this size the stop is forced off regardless of `withStop`. */
const MIN_STOP_SIZE = 14;

/** The stop is the Lens ring-dot at 0.46em, sitting a hair below the baseline so it reads as punctuation. */
const STOP_EM = 0.46;

/**
 * The askRichard wordmark: Suisse Int'l set in two weights so the name
 * parses as "ask" + "Richard" rather than one flat word, ending in the Lens
 * ring-dot as its full stop. That is the whole system: the point at the end
 * of askRichard is the Lens. Always one word, askRichard, rendered as a single
 * accessible unit rather than two separate text nodes a screen reader would
 * announce apart.
 */
export default function AskRichardWordmark({
  size = 28,
  inverted = false,
  withStop = true,
  className,
}: AskRichardWordmarkProps) {
  const showStop = withStop && size >= MIN_STOP_SIZE;
  const askColor = inverted ? BRAND_COLORS.sandyBeige : BRAND_COLORS.oceanTeal;
  const richardColor = inverted ? BRAND_COLORS.compassWhite : BRAND_COLORS.jetBlack;
  const stopSize = Math.round(size * STOP_EM);

  return (
    <span
      role="img"
      aria-label="askRichard"
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        fontFamily: FONT_STACK_SUISSE_INTL,
        fontSize: size,
        lineHeight: 1,
        letterSpacing: '-0.02em',
      }}
    >
      <span style={{ fontWeight: 400, color: askColor }}>ask</span>
      <span style={{ fontWeight: 600, color: richardColor }}>Richard</span>
      {showStop && (
        <span
          aria-hidden="true"
          data-testid="askrichard-stop"
          style={{
            display: 'inline-block',
            width: stopSize,
            height: stopSize,
            marginLeft: '0.1em',
            transform: 'translateY(0.09em)',
            lineHeight: 0,
          }}
        >
          <LensMark size={stopSize} inverted={inverted} />
        </span>
      )}
    </span>
  );
}
